import React from 'react';
import { useTranslation } from 'react-i18next';
import { getPresetRange, formatBerlinDisplay } from '../../../utils/berlinDate';
import {
  CALENDAR_PRESET_KEYS,
  ROLLING_PRESET_KEYS,
  ALL_DATE_PRESET_KEYS,
  DATE_PRESET_GROUPS,
  presetLabelKey,
} from '../../../utils/datePresets';

const PILL_CLASS =
  'px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-full ' +
  'text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-colors';

const [CALENDAR_GROUP, ROLLING_GROUP] = DATE_PRESET_GROUPS;

/**
 * DateRangePresets - quick-select buttons for a date range filter.
 *
 * Both groups are calendar-day-aligned against Europe/Berlin, and are time-
 * sensitive: "now"-anchored boundaries use the exact current instant, and
 * calendar boundaries use DST-aware Berlin midnight/end-of-day timestamps
 * (see utils/berlinDate.js).
 *
 * The resolved startDate/endDate are re-resolved fresh from `presetKey` at
 * request time (see services/helpers/filterRequest.js) so the range keeps
 * rolling forward day to day instead of freezing at the values resolved here
 * — this component only needs to record which preset was picked.
 *
 * Preset KEYS come from utils/datePresets.js and are language-invariant; only
 * their labels are translated, at render time. Nothing here may store a
 * translated string — see the note on lastPresetKey below.
 *
 * @param {object} value - Current dateRange value { startDate, endDate, enabled, presetKey? }
 * @param {Function} onChange - Callback invoked with the updated dateRange value
 */
const DateRangePresets = ({ value, onChange }) => {
  const { t } = useTranslation();

  // Holds the KEY, never the label. Storing the resolved label here (as this
  // did previously) froze the badge text at click time — and after i18n it
  // would also have frozen it in the language active at that moment, going
  // stale on every language switch.
  const [lastPresetKey, setLastPresetKey] = React.useState(() =>
    ALL_DATE_PRESET_KEYS.includes(value?.presetKey) ? value.presetKey : null,
  );

  const applyPreset = (key) => {
    const { startDate, endDate } = getPresetRange(key);
    onChange({ ...value, startDate, endDate, enabled: true, presetKey: key });
    setLastPresetKey(key);
  };

  const appliedStart = formatBerlinDisplay(value?.startDate);
  const appliedEnd = formatBerlinDisplay(value?.endDate);

  const renderGroup = (labelKey, keys) => (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-gray-500">{t(labelKey)}</label>
      <div className="flex flex-wrap gap-2">
        {keys.map((key) => (
          <button key={key} type="button" onClick={() => applyPreset(key)} className={PILL_CLASS}>
            {t(presetLabelKey(key))}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {renderGroup(CALENDAR_GROUP.labelKey, CALENDAR_PRESET_KEYS)}
      {renderGroup(ROLLING_GROUP.labelKey, ROLLING_PRESET_KEYS)}

      {value?.enabled && (appliedStart || appliedEnd) && (
        <div className="text-xs text-gray-700 bg-blue-50 border border-blue-200 rounded px-3 py-2">
          {lastPresetKey && (
            <span className="font-medium">
              {t('filter:dateRange.appliedPresetPrefix', { label: t(presetLabelKey(lastPresetKey)) })}
            </span>
          )}
          {/* '–' is a typographic placeholder for an empty date, not text */}
          <span>{appliedStart || '–'}</span>
          <span className="text-gray-400">{t('common:datePresets.rangeSeparator')}</span>
          {/* '–' is a typographic placeholder for an empty date, not text */}
          <span>{appliedEnd || '–'}</span>
        </div>
      )}
    </div>
  );
};

export default DateRangePresets;
